import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const disabled = error.response?.status === 403 &&
      String(error.response?.data?.detail || '').startsWith('Account is disabled');
    // A wrong password on the login form is a normal 401: let the form show it.
    const isLogin = (error.config?.url || '').includes('/auth/login');
    if (!isLogin && (error.response?.status === 401 || disabled)) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  changePassword: (current_password, new_password) =>
    api.post('/auth/change-password', { current_password, new_password }),
};

// Users APIs — admin-only on the backend (403 for non-admins).
export const usersAPI = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, new_password) =>
    api.post(`/users/${id}/reset-password`, { new_password }),
};

// Employee APIs
export const employeeAPI = {
  getAll: () => api.get('/employees'),
  getOne: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  getDepartmentStats: () => api.get('/departments/stats'),
};

// Service APIs
export const serviceAPI = {
  getAll: () => api.get('/services'),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
};

// Package APIs
export const packageAPI = {
  getAll: () => api.get('/packages'),
  create: (data) => api.post('/packages', data),
  update: (id, data) => api.put(`/packages/${id}`, data),
  delete: (id) => api.delete(`/packages/${id}`),
};

// Client APIs
export const clientAPI = {
  getAll: () => api.get('/clients'),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Proposal APIs
export const proposalAPI = {
  getAll: () => api.get('/proposals'),
  getOne: (id) => api.get(`/proposals/${id}`),
  create: (data) => api.post('/proposals', data),
  update: (id, data) => api.put(`/proposals/${id}`, data),
  delete: (id) => api.delete(`/proposals/${id}`),
  downloadPdf: (id) => api.get(`/proposals/${id}/pdf`, { responseType: 'blob' }),
};

// Invoice APIs — GST-compliant tax invoices
export const invoiceAPI = {
  getAll: () => api.get('/invoices'),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  downloadPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  recordPayment: (id, data) => api.post(`/invoices/${id}/payments`, data),
  listPayments: (id) => api.get(`/invoices/${id}/payments`),
  // body: { channels: ['email','whatsapp'], email?: 'a@x.com, b@y.com', phone?: '+91…' }
  // -> { results: [{channel, status: 'sent'|'failed'|'skipped', to, error?}], invoice }
  send: (id, body) => api.post(`/invoices/${id}/send`, body),
  remind: (id, body) => api.post(`/invoices/${id}/remind`, body),
  notifications: (id) => api.get(`/invoices/${id}/notifications`),
  // who the invoice would go to, resolved by the same rules the sender uses -> { client_name, email, whatsapp }
  recipients: (id) => api.get(`/invoices/${id}/recipients`),
  // every PDF that was actually sent for this invoice, and the exact bytes of one of them
  documents: (id) => api.get(`/invoices/${id}/documents`),
  downloadDocument: (id, pdfId) => api.get(`/invoices/${id}/documents/${pdfId}`, { responseType: 'blob' }),
  cancel: (id) => api.post(`/invoices/${id}/cancel`),
  reinstate: (id) => api.post(`/invoices/${id}/reinstate`),
};

// Client-level payments (unallocated receipts against a client)
export const clientPaymentsAPI = {
  record: (clientId, data) => api.post(`/clients/${clientId}/payments`, data),
};

// Credit notes — delete is admin-only on the backend.
export const creditNotesAPI = {
  getAll: () => api.get('/credit-notes'),
  create: (data) => api.post('/credit-notes', data),
  refund: (id) => api.post(`/credit-notes/${id}/refund`),
  delete: (id) => api.delete(`/credit-notes/${id}`),
};

// Vendors — delete is admin-only on the backend.
export const vendorsAPI = {
  getAll: () => api.get('/vendors'),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
};

// Email (Resend) + WhatsApp (Meta Cloud API) status, reminder settings + log.
export const notificationsAPI = {
  status: () => api.get('/notifications/status'),
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
  preview: () => api.get('/notifications/preview'),
  recent: (limit = 50) => api.get('/notifications/recent', { params: { limit } }),
  // admin only: one harmless message to an address/number you type. channel: 'email' | 'whatsapp'
  sendTest: (channel, to) => api.post('/notifications/test', { channel, to }),
};

// Admin one-shot data tools
export const adminAPI = {
  mergeDuplicateClients: (dry_run = true) =>
    api.post('/admin/clients/merge-duplicates', { dry_run }),
  mergeClients: (keep_id, merge_ids) => api.post('/admin/clients/merge', { keep_id, merge_ids }),
};

// Standalone payments
export const paymentAPI = {
  delete: (id) => api.delete(`/payments/${id}`),
  // Receipts (rows of one receipt grouped); params: client_id, date_start, date_end
  list: (params) => api.get('/payments', { params }),
  receiptPdf: (id) => api.get(`/payments/${id}/receipt.pdf`, { responseType: 'blob' }),
  statementPdf: (params) => api.get('/payments/statement.pdf', { params, responseType: 'blob' }),
};

// Client ledger + statement-of-accounts PDF
// DESENO (partnership) only — Pvt Ltd billing moved to Tally.
export const ledgerAPI = {
  get: (clientId, { date_start, date_end } = {}) =>
    api.get(`/clients/${clientId}/ledger`, { params: { date_start, date_end } }),
  statementPdf: (clientId, { date_start, date_end } = {}) =>
    api.get(`/clients/${clientId}/statement.pdf`, {
      params: { date_start, date_end },
      responseType: 'blob',
    }),
};

// Expense APIs
export const expenseAPI = {
  getAll: () => api.get('/expenses'),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

// Account APIs
export const accountAPI = {
  getAll: () => api.get('/accounts'),
  create: (data) => api.post('/accounts', data),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  delete: (id) => api.delete(`/accounts/${id}`),
};

// Statement APIs (PDF upload + bulk expense rows from one statement)
export const statementAPI = {
  getAll: () => api.get('/statements'),
  get: (id) => api.get(`/statements/${id}`),
  delete: (id) => api.delete(`/statements/${id}`),
  upload: (accountId, period, notes, pdfFile) => {
    const form = new FormData();
    form.append('account_id', accountId);
    form.append('period', period || '');
    form.append('notes', notes || '');
    form.append('pdf', pdfFile);
    return api.post('/statements', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  pdfBlobUrl: async (id) => {
    const res = await api.get(`/statements/${id}/pdf`, { responseType: 'blob' });
    return URL.createObjectURL(res.data);
  },
  listExpenses: (id) => api.get(`/statements/${id}/expenses`),
  saveExpenses: (id, rows, replaceExisting = true) =>
    api.post(`/statements/${id}/expenses`, { rows, replace_existing: replaceExisting }),
};

// Settings APIs
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Workforce monitor — PriceIntel backend proxies the VPS ingest service,
// joining machine data with employees/departments/costs. Admin-only.
export const workforceAPI = {
  status: () => api.get('/workforce/status'),
  test: (base_url, read_key) => api.post('/workforce/test', { base_url, read_key }),
  overview: (params) => api.get('/workforce/overview', { params: typeof params === 'string' ? { date: params } : params }),
  person: (employeeId, date_start, date_end) =>
    api.get(`/workforce/person/${employeeId}`, { params: { date_start, date_end } }),
  departments: (date_start, date_end) =>
    api.get('/workforce/departments', { params: { date_start, date_end } }),
  machines: () => api.get('/workforce/machines'),
  setMachineMap: (machine_id, employee_id) =>
    api.post('/workforce/machine-map', { machine_id, employee_id }),
  deleteMachineMap: (machineId) => api.delete(`/workforce/machine-map/${machineId}`),
  autoMap: () => api.post('/workforce/auto-map'),
};

// Dashboard APIs
// The app is DESENO (partnership) only; the backend enforces it, `entity` is ignored.
// `range` is optional { start, end } in YYYY-MM-DD; either side can be null.
const _dashboardParams = (entity, range) => {
  const params = {};
  if (range?.start) params.date_start = range.start;
  if (range?.end) params.date_end = range.end;
  return Object.keys(params).length ? { params } : undefined;
};

export const dashboardAPI = {
  getStats: (entity, range) => api.get('/dashboard/stats', _dashboardParams(entity, range)),
  getRevenueBreakdown: (entity, range) => api.get('/dashboard/revenue-breakdown', _dashboardParams(entity, range)),
  getExpenseBreakdown: (entity, range) => api.get('/dashboard/expense-breakdown', _dashboardParams(entity, range)),
  getMonthlyTrends: (entity, range) => api.get('/dashboard/monthly-trends', _dashboardParams(entity, range)),
  getProfitBreakdown: (entity, range) => api.get('/dashboard/profit-breakdown', _dashboardParams(entity, range)),
};

// Calculator API
export const calculatorAPI = {
  estimate: (data) => api.post('/calculator/estimate', data),
};

// Calendar API
export const calendarAPI = {
  getDailySummary: (month, year) => api.get('/calendar/daily-summary', { params: { month, year } }),
  getEvents: (month, year) => api.get('/calendar/events', { params: { month, year } }),
};

// Finance API — cash-reserve / runway model
export const financeAPI = {
  cashReserve: (params) => api.get('/finance/cash-reserve', { params }),
};

// Income API
export const incomeAPI = {
  getAll: () => api.get('/income'),
  create: (data) => api.post('/income', data),
  delete: (id) => api.delete(`/income/${id}`),
};


export default api;
